import React, { useState } from "react";

import dayjs, { Dayjs } from "dayjs";

import { useTheme } from "@emotion/react";
import styled from "@emotion/styled";
import { useDebounce } from "use-debounce";
import shallow from "zustand/shallow";

import Bold from "../../../../shared/Bold";
import { IconPredefined } from "../../../../shared/icons/Icon";
import { PredefinedIconType } from "../../../../shared/icons/Icon/Predefined";
import Button, { ButtonColor } from "../../../../shared/NewForms/Button";
import HoyerAclError from "../../../../shared/HoyerAclError";
import Pagination from "../../DynPin/components/Pagination";
import DateRangeCalendar, {
    dayjsOrNull,
} from "../../components/DateRangeCalendar";
import TransactionsDownload from "../../components/Downloads/TransactionsDownload";
import Panel from "../../components/Panel";
import SimpleModal from "../../components/SimpleModal";
import TransactionList from "../../components/TransactionList";
import {
    TransactionPermission,
    useHoyerAclStore,
} from "../../hooks/useHoyerAcl";
import useCustomerTransactions from "../../queries/useCustomerTransactions";
import useCustomerTransactionsSummary from "../../queries/useCustomerTransactionsSummary";
import useTransactionStore, {
    TransactionDateStatus,
} from "../../Transactions/store";
import useStore from "../store";
import Search from "./Search";
import Sort from "./Sort";

const Toolbar = styled.div`
    display: flex;
    flex-direction: row;
    flex-wrap: wrap;
    justify-content: space-between;
    align-items: flex-start;
    gap: 14px;
    margin-bottom: 18px;
`;

const ActionGroup = styled.div`
    display: flex;
    flex-direction: row;
    flex-wrap: wrap;
    flex: 0 0 auto;
    align-items: center;
    gap: 14px;
`;

// Scoped to this toolbar only — matches the search/sort/filter field look
// from the click-dummy (navy 1px border, 4px radius, 15px padding).
const FilterGroup = styled.div`
    display: flex;
    flex-direction: row;
    flex-wrap: nowrap;
    align-items: flex-start;
    gap: 14px;

    & div {
        margin-top: 0 !important;
        margin-bottom: 0 !important;
    }

    input {
        border: 1px solid ${({ theme }) => theme.colors.blue.toString()} !important;
        border-radius: 4px !important;
        padding: 15px 56px 15px 16px !important;
        font-size: 15px !important;
        color: ${({ theme }) => theme.colors.blue.toString()} !important;

        &:focus {
            border-color: ${({ theme }) =>
                theme.colors.blue.toString()} !important;
        }
    }

    /* NewSelect's own padding reserves vertical room for its floating
       "Sortieren" label above the value — accessibility-standardized
       input/select height across the app, don't shrink without checking
       with the team first (see 2026-08-12 discussion). */
    select {
        border: 1px solid ${({ theme }) => theme.colors.blue.toString()} !important;
        border-radius: 4px !important;
        color: ${({ theme }) => theme.colors.blue.toString()} !important;

        &:focus {
            border-color: ${({ theme }) =>
                theme.colors.blue.toString()} !important;
        }
    }
`;

const SearchBox = styled.div`
    position: relative;
    flex: 0 0 280px;
`;

const SortBox = styled.div`
    flex: 0 0 auto;
`;

// Sits inside SearchBox, overlaying the search input — matches the
// click-dummy, where the calendar icon lives inside the "Suche / Filter"
// field itself instead of being its own separate button. Offset from the
// very right edge (not right: 0) so it doesn't collide with the input's
// own "clear search" (X) icon, which claims that outermost corner once
// the user has typed something.
const DateFilterButton = styled.button`
    position: absolute;
    top: 0;
    right: 12px;
    bottom: 0;
    display: flex;
    align-items: center;
    padding: 0 6px;
    background: transparent;
    border: none;
    cursor: pointer;
`;

const Presets = styled.div`
    display: flex;
    flex-wrap: wrap;
    gap: 12px;
    margin-bottom: 20px;
`;

const Preset = styled.button<{ active: boolean }>`
    padding: 8px 18px;
    border: 1px solid ${({ theme }) => theme.colors.blue.toString()};
    border-radius: 20px;
    font-size: 13.5px;
    font-family: inherit;
    cursor: pointer;
    background: ${({ active, theme }) =>
        active ? theme.colors.blue.toString() : "transparent"};
    color: ${({ active, theme }) =>
        active ? theme.colors.white.toString() : theme.colors.blue.toString()};
`;

const Actions = styled.div`
    display: flex;
    justify-content: center;
    gap: 14px;
    margin-top: 30px;
`;

const ModalTitle = styled.div`
    margin-bottom: 22px;
    font-family: var(--font-family-vito);
    font-size: 26px;
    font-weight: 900;
    color: ${({ theme }) => theme.colors.blue.toString()};
`;

const DATE_PRESETS: { status: TransactionDateStatus; label: string }[] = [
    { status: "week", label: "Diese Woche" },
    { status: "lastweek", label: "Letzte Woche" },
    { status: "month", label: "Diesen Monat" },
    { status: "lastmonth", label: "Letzten Monat" },
];

const DATE_STATUS_LABELS: Record<TransactionDateStatus, string> = {
    week: "Diese Woche",
    lastweek: "Letzte Woche",
    month: "Diesen Monat",
    lastmonth: "Letzten Monat",
    custom: "Benutzerdefiniert",
};

// Client-side mirror of TransactionRangeResolver's week/month math, used
// only to preview the highlighted range in the calendar — the backend
// resolves the authoritative boundaries from the submitted `status`.
const presetRange = (status: TransactionDateStatus) => {
    const now = dayjs();
    switch (status) {
        case "week":
            return [now.startOf("week"), now.endOf("week")];
        case "lastweek": {
            const lastWeek = now.subtract(1, "week");
            return [lastWeek.startOf("week"), lastWeek.endOf("week")];
        }
        case "month":
            return [now.startOf("month"), now.endOf("month")];
        case "lastmonth": {
            const lastMonth = now.subtract(1, "month");
            return [lastMonth.startOf("month"), lastMonth.endOf("month")];
        }
        default:
            return null;
    }
};

export const TransactionDateFilter = () => {
    const theme = useTheme();
    const [dateStatus, dateStart, dateEnd, setDateRange] = useTransactionStore(
        (s) => [s.dateStatus, s.dateStart, s.dateEnd, s.setDateRange],
        shallow
    );
    const setPage = useTransactionStore((s) => s.setPage);

    const [open, setOpen] = useState(false);
    const [pendingStatus, setPendingStatus] =
        useState<TransactionDateStatus | null>(dateStatus);
    const [pendingStart, setPendingStart] = useState(dateStart);
    const [pendingEnd, setPendingEnd] = useState(dateEnd);
    const [viewMonth, setViewMonth] = useState(() =>
        (dayjsOrNull(dateStart) ?? dayjs()).startOf("month")
    );

    const openModal = () => {
        setPendingStatus(dateStatus);
        setPendingStart(dateStart);
        setPendingEnd(dateEnd);
        setViewMonth((dayjsOrNull(dateStart) ?? dayjs()).startOf("month"));
        setOpen(true);
    };

    const selectPreset = (status: TransactionDateStatus) => {
        setPendingStatus(status);
        const range = presetRange(status);
        if (range) {
            const [start, end] = range;
            setPendingStart(start.format("YYYY-MM-DD"));
            setPendingEnd(end.format("YYYY-MM-DD"));
            setViewMonth(start.startOf("month"));
        }
    };

    const handleDayClick = (day: Dayjs) => {
        setPendingStatus("custom");
        const start = dayjsOrNull(pendingStart);
        const alreadyComplete = !!start && !!dayjsOrNull(pendingEnd);
        if (!start || alreadyComplete || day.isBefore(start, "day")) {
            setPendingStart(day.format("YYYY-MM-DD"));
            setPendingEnd("");
            return;
        }
        setPendingEnd(day.format("YYYY-MM-DD"));
    };

    const apply = () => {
        // A single clicked day (no second click yet) selects just that one day.
        setDateRange(pendingStatus, pendingStart, pendingEnd || pendingStart);
        setPage(1);
        setOpen(false);
    };

    const reset = () => {
        setDateRange(null);
        setPage(1);
        setOpen(false);
    };

    const isCustomInvalid =
        pendingStatus === "custom" &&
        (!pendingStart || (!!pendingEnd && pendingEnd < pendingStart));

    return (
        <>
            <DateFilterButton
                type="button"
                onClick={openModal}
                aria-label={
                    dateStatus
                        ? `Zeitraum: ${DATE_STATUS_LABELS[dateStatus]}`
                        : "Zeitraum wählen"
                }
                title={
                    dateStatus ? DATE_STATUS_LABELS[dateStatus] : "Zeitraum wählen"
                }
            >
                <IconPredefined
                    type={PredefinedIconType.calendar}
                    size="17px"
                    color={theme.colors.blue.toString()}
                />
            </DateFilterButton>

            <SimpleModal isOpen={open} setIsOpen={setOpen}>
                <ModalTitle>Zeitraum wählen</ModalTitle>
                <Presets>
                    {DATE_PRESETS.map(({ status, label }) => (
                        <Preset
                            key={status}
                            type="button"
                            active={pendingStatus === status}
                            onClick={() => selectPreset(status)}
                        >
                            {label}
                        </Preset>
                    ))}
                </Presets>

                <DateRangeCalendar
                    viewMonth={viewMonth}
                    onViewMonthChange={setViewMonth}
                    rangeStart={dayjsOrNull(pendingStart)}
                    rangeEnd={dayjsOrNull(pendingEnd)}
                    onDayClick={handleDayClick}
                />

                <Actions>
                    <Button color={ButtonColor.blueOutline} onClick={reset}>
                        Zurücksetzen
                    </Button>
                    <Button
                        color={ButtonColor.blue}
                        onClick={apply}
                        disabled={!pendingStatus || isCustomInvalid}
                    >
                        Anwenden
                    </Button>
                </Actions>
            </SimpleModal>
        </>
    );
};

const Footer = styled.div`
    display: flex;
    flex-wrap: wrap;
    justify-content: space-between;
    align-items: center;
    margin-top: 18px;
    font-size: 13.5px;
`;

const Transaction = ({ ...props }) => {
    const { can } = useHoyerAclStore();

    const [selectedCustomerNumber] = useStore(
        (state) => [state.selectedCustomerNumber],
        shallow
    );

    const [filter, sort, sortDesc, page, setPage, dateStatus, dateStart, dateEnd] =
        useTransactionStore(
            (state) => [
                state.filter,
                state.sort,
                state.sortDesc,
                state.page,
                state.setPage,
                state.dateStatus,
                state.dateStart,
                state.dateEnd,
            ],
            shallow
        );

    const [debouncedFilter] = useDebounce(filter, 500);

    const { data, error, isLoading } = useCustomerTransactions(
        selectedCustomerNumber,
        debouncedFilter,
        sort,
        sortDesc,
        page,
        dateStatus,
        dateStart,
        dateEnd
    );

    const { data: summary } = useCustomerTransactionsSummary(
        selectedCustomerNumber,
        debouncedFilter,
        dateStatus,
        dateStart,
        dateEnd
    );

    if (!can(TransactionPermission.LIST)) {
        return (
            <HoyerAclError message="Du hast keine Berechtigung Transaktionen anzusehen." />
        );
    }

    const transactions = data?.data ?? [];

    return (
        <Panel title="Transaktionen">
            <Toolbar>
                <FilterGroup>
                    <SearchBox>
                        <TransactionSearch />
                        <TransactionDateFilter />
                    </SearchBox>
                    <SortBox>
                        <TransactionSort />
                    </SortBox>
                </FilterGroup>
                <ActionGroup>
                    <TransactionsDownload />
                </ActionGroup>
            </Toolbar>

            {selectedCustomerNumber && (
                <>
                    {error && (
                        <div>
                            Beim Laden der Transaktionen ist ein Fehler
                            aufgetreten.
                        </div>
                    )}

                    <TransactionList
                        transactions={transactions}
                        limited={false}
                        isLoading={isLoading}
                    />

                    {!isLoading && transactions.length > 0 && (
                        <Footer>
                            <span>
                                Insgesamt <Bold>{data?.meta.total}</Bold>{" "}
                                Transaktionen
                                {summary && summary.products.length > 0 && (
                                    <>
                                        {summary.products.map((p) => (
                                            <React.Fragment key={p.product}>
                                                {", "}
                                                <Bold>
                                                    {p.totalQuantity.toLocaleString(
                                                        "de-DE",
                                                        {
                                                            maximumFractionDigits: 0,
                                                        }
                                                    )}
                                                </Bold>{" "}
                                                Liter {p.product}
                                            </React.Fragment>
                                        ))}
                                    </>
                                )}
                            </span>
                            {data && (
                                <Pagination
                                    paginationLinks={data.links}
                                    paginationMeta={data.meta}
                                    setPage={setPage}
                                />
                            )}
                        </Footer>
                    )}
                </>
            )}
        </Panel>
    );
};

export default Transaction;

export const TransactionSearch = () => {
    const [setFilter, setPage] = useTransactionStore(
        (s) => [s.setFilter, s.setPage],
        shallow
    );
    return (
        <Search
            onSearch={(search) => {
                setFilter(search);
                setPage(1);
            }}
        />
    );
};

export const TransactionSort = () => {
    const [sort, setSort, setSortDesc, sortDesc] = useTransactionStore(
        (s) => [s.sort, s.setSort, s.setSortDesc, s.sortDesc],
        shallow
    );

    const sortBy = [
        { value: "timestamp", text: "Datum" },
        { value: "card_number", text: "Kartennummer" },
        { value: "currency_amount", text: "Betrag" },
        { value: "product_quantity", text: "Menge" },
        { value: "product_desc", text: "Produkt" },
        { value: "ifsf_name", text: "Station" },
        { value: "kfz_kennzeichen", text: "Kennzeichen" },
    ];

    return (
        <Sort
            sort={sort}
            sortBy={sortBy}
            setSort={setSort}
            setSortDesc={setSortDesc}
            sortDesc={sortDesc}
        />
    );
};
